import React, { useCallback, useContext, useEffect } from 'react'
import { defaultObjData } from 'utility/useStateDefaults'
import axios, { UNAUTHORIZED_EVENT } from 'utility/axios'
import { GET_AUTHTOKEN, GET_USER_ME } from 'utility/endpoints'
import { useLocalStorage } from 'hooks/useLocalStorage'
import { useApp } from 'components/providers/AppProvider'
import { formatUserObject } from 'utility/object'
import { useRouter } from 'next/router'
import * as crisp from 'utility/crisp'

const GroupContext = React.createContext(null)

const SelfProvider = ({ children }) => {
	const router = useRouter()

	const { authToken, setAuthToken } = useApp()
	const [selfUser, setSelfUser, isSelfUserHydrated] = useLocalStorage('selfUser', defaultObjData)

	useEffect(() => {
		if (!authToken && selfUser && Object.keys(selfUser?.data).length) {
			logOut()
		}
	}, [selfUser, authToken])

	useEffect(() => {
		if (authToken) {
			// Errors are already captured in selfUser.error
			fetchSelfFromAuthToken(authToken).catch(() => {})
		}
	}, [fetchSelfFromAuthToken, authToken])

	const loginFromToken = async (loginToken) => {
		try {
			let response = await axios.get(`${GET_AUTHTOKEN}${loginToken}`)
			setAuthToken(response?.data)
			return response
		} catch (err) {
			// Pass the error to the response
			throw err
		}
	}

	const fetchSelfFromAuthToken = useCallback(
		async (intAuthToken) => {
			let headers = {}
			if (intAuthToken) {
				headers = {
					'Auth-Token': intAuthToken,
				}
			}

			try {
				setSelfUser((prev) => ({ ...prev, loading: true }))
				let response = await axios.get(GET_USER_ME, {
					headers,
				})
				// Set the selfUser here...
				setSelfUser((prev) => ({ ...prev, data: response.data, loading: false }))

				const email = response.data?.profile?.emailAddress
				const phoneNumber = response.data?.profile?.phoneNumber

				crisp.identify('email', email)
				crisp.identify('phone', phoneNumber)

				return response.data
			} catch (err) {
				setSelfUser((prev) => ({ ...prev, error: err, loading: false }))
				// Only discard the token when the backend rejected it —
				// transient network/server errors must not log the user out
				const status = err?.response?.status
				if (status === 401 || status === 403) {
					setAuthToken('')
				}
				// Pass the error to the response
				throw err
			}
		},
		[setSelfUser, setAuthToken],
	)

	// Set authToken to '', then watch and log selfUser out
	const logOut = useCallback(() => {
		setAuthToken('')
		router.push('/auth/signin')
	}, [setAuthToken, router])

	// The axios response interceptor announces rejected tokens from outside React
	useEffect(() => {
		window.addEventListener(UNAUTHORIZED_EVENT, logOut)
		return () => window.removeEventListener(UNAUTHORIZED_EVENT, logOut)
	}, [logOut])

	useEffect(() => {
		if (!authToken) {
			setSelfUser(defaultObjData)
		}
	}, [authToken])

	const isAdmin =
		selfUser?.data?.isSuperAdmin === 'Yes' || selfUser?.data?.isTenantAdmin === 'Yes' ? true : false

	const isSignedIn = authToken && selfUser && Object.keys(selfUser?.data).length > 0 ? true : false

	const isBlocked = selfUser?.data?.isBlocked === 'Yes' ? true : false

	const selfValue = formatUserObject({ ...selfUser.data, isSignedIn })

	const isHomeowner = selfValue?.groupAccounts && selfValue?.groupAccounts.length > 0
	const isPartner = selfValue?.partnerAccounts && selfValue?.partnerAccounts.length > 0

	if (!isSelfUserHydrated) return null

	return (
		<GroupContext.Provider
			value={{
				loginFromToken,
				fetchSelfFromAuthToken,
				logOut,
				isSignedIn,
				user: selfValue,
				isAdmin,
				isHomeowner,
				isBlocked,
				isPartner,
				authToken,
			}}
		>
			{children}
		</GroupContext.Provider>
	)
}

const useSelf = () => {
	const auth = useContext(GroupContext)
	if (auth == null) {
		throw new Error('useSelf() called outside of a SelfProvider?')
	}
	return auth
}

export { SelfProvider, useSelf }
